/** @jsxImportSource preact */
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { DocumentDetailContent, type DocumentDetail } from './DocumentDetailModal';

export interface DocumentDetailInlineProps {
  apiBase: string;
  documentId: string;
  /**
   * CSS selector for the host page's server-rendered version of the same
   * content (the Webflow CMS fields). Hidden only once the document has
   * loaded, so a failed fetch or disabled JavaScript leaves it in place.
   */
  fallback?: string;
}

/**
 * The document detail body rendered inline on a per-document page
 * (airisk.mit.edu/governance-documents/<slug>), using the same component as
 * the Document View modal. Renders nothing until the document has loaded.
 */
export function DocumentDetailInline({ apiBase, documentId, fallback }: DocumentDetailInlineProps) {
  const [doc, setDoc] = useState<DocumentDetail | null>(null);

  useEffect(() => {
    if (!/^\d+$/.test(documentId)) {
      console.error(`DocumentDetailInline: invalid documentId ${JSON.stringify(documentId)}`);
      return;
    }
    let cancelled = false;
    fetch(`${apiBase}/data/risk-subdomain/document?documentId=${encodeURIComponent(documentId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json: { document?: DocumentDetail | null }) => {
        if (!cancelled && json?.document) setDoc(json.document);
      })
      .catch((err) => console.error('DocumentDetailInline: failed to load document', err));
    return () => { cancelled = true; };
  }, [apiBase, documentId]);

  useEffect(() => {
    if (!doc || !fallback) return;
    document.querySelectorAll<HTMLElement>(fallback).forEach((el) => { el.style.display = 'none'; });
  }, [doc, fallback]);

  if (!doc) return null;
  return <DocumentDetailContent documentId={documentId} document={doc} loading={false} hideTitle />;
}
